import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api, ApiError, type ApiUser } from "@/lib/api";
import {
  AdminAuthContext,
  type AdminUser,
} from "@/providers/admin-auth-context";

function toAdminUser(user: ApiUser): AdminUser | null {
  if (user.role !== "ADMIN" || !user.isActive) {
    return null;
  }
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
  };
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const me = await api.getMe();
        if (!cancelled) {
          setAdminUser(toAdminUser(me));
        }
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          try {
            const refreshed = await api.refreshSession();
            if (!cancelled) {
              setAdminUser(toAdminUser(refreshed.user));
            }
          } catch {
            if (!cancelled) {
              setAdminUser(null);
            }
          }
        } else if (!cancelled) {
          // Includes 403 when allowlist no longer includes this admin
          setAdminUser(null);
        }
      } finally {
        if (!cancelled) {
          setIsBootstrapping(false);
        }
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const loginWithGoogle = useCallback(async (idToken: string) => {
    const result = await api.loginWithGoogle(idToken);
    const nextUser = toAdminUser(result.user);
    if (!nextUser) {
      throw new Error(
        "This Google account is not authorized for admin access.",
      );
    }
    setAdminUser(nextUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Clear local session even if the API call fails (expired cookie, etc.)
    } finally {
      setAdminUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      adminUser,
      isAuthenticated: adminUser !== null,
      isBootstrapping,
      loginWithGoogle,
      logout,
    }),
    [adminUser, isBootstrapping, loginWithGoogle, logout],
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}
